import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { FlashIcon } from "../icons/FlashIcon";
import { CheckCircle } from "../icons/CheckCircle";
import backpackSvgImport from "./backback.svg";
import forkRoadSvgImport from "./fork-road.svg";
import toolboxSvgImport from "./toolbox.svg";
import balanceSvgImport from "./balance.svg";
import vineTopSvgImport from "./vine-top.svg";
import vineMidSvgImport from "./vine-mid.svg";
import vineBottomSvgImport from "./vine-bottom.svg";
import {
  PERKS_QUIRKS_XP,
  JOB_GEAR_XP,
  HIGH_SCHOOL_PRIORITIES_XP,
  AFTER_HIGH_SCHOOL_XP,
  ACTIVITY_PROMPTS,
} from "../../constants";
import { toSrc } from "../../utils/toSrc";
import type { ActivityType, ActivityCompletion, MSHatData } from "../../types";

const backpackSvg = toSrc(backpackSvgImport as string | { src: string });
const forkRoadSvg = toSrc(forkRoadSvgImport as string | { src: string });
const toolboxSvg = toSrc(toolboxSvgImport as string | { src: string });
const balanceSvg = toSrc(balanceSvgImport as string | { src: string });
const vineTopSvg = toSrc(vineTopSvgImport as string | { src: string });
const vineMidSvg = toSrc(vineMidSvgImport as string | { src: string });
const vineBottomSvg = toSrc(vineBottomSvgImport as string | { src: string });

interface ActivityPanelProps {
  hat: MSHatData;
  activityCompletions?: Record<string, ActivityCompletion>;
  onActivityClick: (activityType: ActivityType) => void;
  hatId: string;
}

const notEmpty = (arr?: unknown[]): boolean => !!arr && arr.length > 0;

// Which activities have content backing them. Activities with no data are
// hidden entirely (along with any vine segment that would touch them).
const getActivityPresence = (
  hat: MSHatData,
): Record<ActivityType, boolean> => ({
  perksquirks: notEmpty(hat.msPerks) || notEmpty(hat.msQuirks),
  jobgear: notEmpty(hat.msJobGear),
  highschoolpriorities:
    notEmpty(hat.msConnectedCourses) || notEmpty(hat.msKeysToSuccess),
  afterhighschool:
    notEmpty(hat.msPopularPaths) ||
    notEmpty(hat.msOtherRoutes) ||
    notEmpty(hat.msAfterHighSchoolBlurbs),
});

const PanelContainer = styled(Box, {
  label: "ActivityPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  position: "relative",
  paddingLeft: 24,
  paddingRight: 24,
}));

interface ConnectorProps {
  src: string;
  left: number;
  top: number;
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
  rotation: number;
}

const Connector = ({
  src,
  left,
  top,
  width,
  height,
  innerWidth,
  innerHeight,
  rotation,
}: ConnectorProps) => (
  <Box
    sx={{
      position: "absolute",
      left,
      top,
      width,
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        width: innerWidth,
        height: innerHeight,
        transform: `rotate(${rotation}deg)`,
      }}
    >
      <img
        src={src}
        alt=""
        width={innerWidth}
        height={innerHeight}
        style={{ display: "block" }}
      />
    </Box>
  </Box>
);

// Connector "vines" linking the four nodes in instructional order:
// Perks & Quirks → Job Gear → High School Priorities → After High School.
// Vine artwork + transforms are taken directly from the Figma design.
// A vine is only drawn when both activities it connects are present.
const ConnectorLines = ({
  present,
}: {
  present: Record<ActivityType, boolean>;
}) => (
  <>
    {/* Perks & Quirks (top-left) → Job Gear (upper-right) */}
    {present.perksquirks && present.jobgear && (
      <Connector
        src={vineTopSvg}
        left={146.25}
        top={41.44}
        width={74.193}
        height={86.228}
        innerWidth={49.096}
        innerHeight={72.597}
        rotation={156.23}
      />
    )}
    {/* Job Gear (upper-right) → High School Priorities (mid-left) */}
    {present.jobgear && present.highschoolpriorities && (
      <Connector
        src={vineMidSvg}
        left={79.4}
        top={200.45}
        width={125.328}
        height={52.482}
        innerWidth={124.247}
        innerHeight={20.679}
        rotation={164.83}
      />
    )}
    {/* High School Priorities (mid-left) → After High School (lower-right) */}
    {present.highschoolpriorities && present.afterhighschool && (
      <Connector
        src={vineBottomSvg}
        left={143.11}
        top={301.13}
        width={79}
        height={94.736}
        innerWidth={50.532}
        innerHeight={81.262}
        rotation={156.23}
      />
    )}
  </>
);

const ActivityItem = styled(Box, {
  label: "ActivityItem",
})(() => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const ActivityName = styled(Typography, {
  label: "ActivityName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "30px",
  color: "#ffffff",
  textAlign: "center",
  maxWidth: 184,
}));

const ActivityPrompt = styled(Typography, {
  label: "ActivityPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textAlign: "center",
  maxWidth: 184,
}));

const XPBadge = styled(Box, {
  label: "ActivityXPBadge",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 2,
}));

const XPText = styled(Typography, {
  label: "ActivityXPText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#77ed8b",
}));

const CompletedBadge = styled(Box, {
  label: "ActivityCompletedBadge",
})(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
}));

const IconContainer = styled(Box, {
  label: "ActivityIconContainer",
})(() => ({
  width: 64,
  height: 64,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}));

interface ActivityNodeProps {
  name: string;
  prompt: string;
  xpValue: number;
  icon: React.ReactNode;
  isCompleted: boolean;
  onClick: () => void;
  style: React.CSSProperties;
}

const ActivityNode = ({
  name,
  prompt,
  xpValue,
  icon,
  isCompleted,
  onClick,
  style,
}: ActivityNodeProps) => (
  <ActivityItem onClick={onClick} style={style}>
    <IconContainer>
      {icon}
      {isCompleted && (
        <CompletedBadge>
          <CheckCircle width={64} height={64} />
        </CompletedBadge>
      )}
    </IconContainer>
    <ActivityName>{name}</ActivityName>
    <ActivityPrompt>{prompt}</ActivityPrompt>
    <XPBadge>
      <FlashIcon width={16} height={16} color="#77ed8b" />
      <XPText>{xpValue} XP</XPText>
    </XPBadge>
  </ActivityItem>
);

export const ActivityPanel = ({
  hat,
  activityCompletions = {},
  onActivityClick,
  hatId,
}: ActivityPanelProps) => {
  const isCompleted = (activityType: string) =>
    !!activityCompletions[`act-${activityType}-${hatId}`];

  const present = getActivityPresence(hat);

  return (
    <PanelContainer>
      <ConnectorLines present={present} />

      {/* 1. Perks & Quirks - top left */}
      {present.perksquirks && (
        <ActivityNode
          name={"Perks & Quirks"}
          prompt={ACTIVITY_PROMPTS.perksQuirks}
          xpValue={PERKS_QUIRKS_XP}
          icon={
            <img src={balanceSvg} alt="Perks & Quirks" width={64} height={64} />
          }
          isCompleted={isCompleted("perksquirks")}
          onClick={() => onActivityClick("perksquirks")}
          style={{ left: 0, top: 24, width: 184 }}
        />
      )}

      {/* 2. Job Gear - upper right */}
      {present.jobgear && (
        <ActivityNode
          name="Job Gear"
          prompt={ACTIVITY_PROMPTS.jobGear}
          xpValue={JOB_GEAR_XP}
          icon={<img src={toolboxSvg} alt="Job Gear" width={64} height={64} />}
          isCompleted={isCompleted("jobgear")}
          onClick={() => onActivityClick("jobgear")}
          style={{ left: 184, top: 126, width: 184 }}
        />
      )}

      {/* 3. High School Priorities - mid left */}
      {present.highschoolpriorities && (
        <ActivityNode
          name="High School Priorities"
          prompt={ACTIVITY_PROMPTS.highSchoolPriorities}
          xpValue={HIGH_SCHOOL_PRIORITIES_XP}
          icon={
            <img
              src={backpackSvg}
              alt="High School Priorities"
              width={64}
              height={64}
            />
          }
          isCompleted={isCompleted("highschoolpriorities")}
          onClick={() => onActivityClick("highschoolpriorities")}
          style={{ left: 0, top: 284, width: 184 }}
        />
      )}

      {/* 4. After High School - lower right */}
      {present.afterhighschool && (
        <ActivityNode
          name="After High School"
          prompt={ACTIVITY_PROMPTS.afterHighSchool}
          xpValue={AFTER_HIGH_SCHOOL_XP}
          icon={
            <img
              src={forkRoadSvg}
              alt="After High School"
              width={64}
              height={64}
            />
          }
          isCompleted={isCompleted("afterhighschool")}
          onClick={() => onActivityClick("afterhighschool")}
          style={{ left: 184, top: 366, width: 184 }}
        />
      )}
    </PanelContainer>
  );
};
